"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const servicesOptions = ["Individual Tax Preparation", "Business Tax Compliance", "Strategic Financial Planning", "Year-Round Tax Advisory"];
const businessTypes = ["Individual", "Small Business", "Corporation", "Non-Profit"];
const budgetRanges = ["$500 – $1,500", "$1,500 – $3,000", "$3,000 – $5,000", "$5,000+"];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border rounded-md bg-card">
        <p className="font-default text-xs uppercase tracking-widest text-secondary mb-3">// MESSAGE RECEIVED</p>
        <p className="font-default text-2xl text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-default text-xs uppercase tracking-widest rounded-md">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" rules={{
      required: "Name is required",
      minLength: {
        value: 2,
        message: "Min 2 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-secondary">FULL NAME</FormLabel>
            <FormControl>
              <Input placeholder="John Smith" {...field} className="font-default text-sm border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-accent placeholder:text-muted-foreground/60" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" rules={{
      required: "Entity type is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-secondary">ENTITY TYPE</FormLabel>
            <FormControl>
              <Input placeholder="Small Business" {...field} className="font-default text-sm border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-accent placeholder:text-muted-foreground/60" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-secondary">EMAIL ADDRESS</FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" {...field} className="font-default text-sm border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-accent placeholder:text-muted-foreground/60" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-secondary">PHONE NUMBER</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 000-0000" {...field} className="font-default text-sm border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-accent placeholder:text-muted-foreground/60" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="businessType" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-secondary">CLIENT CATEGORY</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="font-default text-sm border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-accent w-full data-[placeholder]:text-muted-foreground/60">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {businessTypes.map((type, i) => <SelectItem key={type} value={type} data-index={i} className="font-default text-sm">
                      {type}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="services" rules={{
      required: "Please select at least one service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-secondary">
              SERVICES REQUIRED
            </FormLabel>
            <FormControl>
              <div className="space-y-3 pt-2">
                {servicesOptions.map((service, i) => {
            const selected: string[] = Array.isArray(field.value) ? field.value : [];
            const isChecked = selected.includes(service);
            return <label data-index={i} key={service} className="flex items-start gap-3 cursor-pointer group">
                      <Checkbox checked={isChecked} onCheckedChange={checked => {
                if (checked) {
                  field.onChange([...selected, service]);
                } else {
                  field.onChange(selected.filter(s => s !== service));
                }
              }} className="mt-0.5" />
                      <span className="font-default text-sm text-foreground/90 group-hover:text-accent transition-colors">
                        {service}
                      </span>
                    </label>;
          })}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-secondary">
              YOUR TAX CONCERNS
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your tax situation or specific questions..." {...field} className="font-default text-sm border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent placeholder:text-muted-foreground/60 min-h-[200px] resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="timeline" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-secondary">URGENCY</FormLabel>
            <FormControl>
              <RadioGroup onValueChange={field.onChange} value={field.value} className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-6">
                {["Urgent", "Standard", "Planning"].map((option, i) => <label data-index={i} key={option} className="flex items-center gap-2 cursor-pointer group">
                    <RadioGroupItem value={option} />
                    <span className="font-default text-sm text-foreground/90 group-hover:text-accent transition-colors">
                      {option}
                    </span>
                  </label>)}
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="budget" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-secondary">ESTIMATED BUDGET</FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="font-default text-sm border-0 border-b border-border bg-transparent px-0 h-11 focus-visible:ring-0 focus-visible:border-accent w-full data-[placeholder]:text-muted-foreground/60">
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  {budgetRanges.map((range, i) => <SelectItem key={range} value={range} data-index={i} className="font-default text-sm">
                      {range}
                    </SelectItem>)}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive/40 rounded-md bg-destructive/10">
          <p className="font-default text-xs text-destructive">{submitError}</p>
        </div>}

      <div className="pt-4 flex flex-col items-stretch sm:items-end gap-4">
        <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto font-default text-xs uppercase tracking-widest rounded-md px-8 h-12 bg-accent text-accent-foreground hover:bg-accent/90 transition-all">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : <>
              Send Request
              <ArrowRight className="ml-2 size-4" />
            </>}
        </Button>
        <p className="font-default text-[10px] uppercase tracking-widest text-muted-foreground/80 max-w-sm sm:text-right">
          Your request is reviewed personally by our tax professionals.
        </p>
      </div>
    </Form>;
}
export default function Inquiry() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const infoBlocks = [{
    label: "Initial Response",
    value: "Within 24 Hours"
  }, {
    label: "Consultation",
    value: "Complimentary"
  }, {
    label: "Service Area",
    value: "Nationwide"
  }];
  return <section data-section-id="3375" id="reach-out-to-us" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-[180px] px-6 md:px-12">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "64px 64px"
    }} aria-hidden="true" />

      <div className="absolute top-1/4 -right-32 w-[600px] h-[600px] rounded-md pointer-events-none opacity-30" style={{
      background: "radial-gradient(circle, var(--accent) 0%, transparent 70%)",
      filter: "blur(120px)"
    }} aria-hidden="true" />

      <div ref={ref} className={`relative max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5 flex flex-col">
            <p className="font-default text-xs uppercase tracking-widest text-secondary mb-8">
              // 05 — Contact
            </p>

            <h2 className="font-default text-4xl md:text-5xl lg:text-6xl leading-[1.05] text-foreground mb-8">
              Let's find your financial <span className="italic font-light">clarity.</span>
            </h2>

            <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed mb-12 max-w-md">
              Connect with Lovejoy Company to discuss your tax compliance and strategic planning needs.
            </p>

            <div className="space-y-6 mt-auto pt-8 border-t border-border">
              {infoBlocks.map((block, i) => <div data-index={i} key={block.label} className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-4">
                  <span className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                    {block.label}
                  </span>
                  <span className="font-default text-xs uppercase tracking-widest text-foreground">
                    {block.value}
                  </span>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="relative">
              <div className="absolute -inset-px rounded-md opacity-50 pointer-events-none" style={{
              background: "linear-gradient(135deg, var(--accent) 0%, transparent 40%, transparent 60%, var(--secondary) 100%)",
              filter: "blur(1px)"
            }} aria-hidden="true" />
              <div className="relative bg-card border border-border rounded-md p-8 md:p-12">
                <div className="flex items-center justify-between mb-10 pb-6 border-b border-border">
                  <span className="font-default text-[10px] uppercase tracking-widest text-muted-foreground">
                    Tax Services Inquiry
                  </span>
                  <span className="font-default text-[10px] uppercase tracking-widest text-accent">
                    LOVEJOY / 2025
                  </span>
                </div>

                <FormProvider formKey="contact_form_get_started" formId="contact_form_get_started" sectionName="reach-out-to-us" defaultValues={{
                name: "",
                company: "",
                email: "",
                phone: "",
                businessType: "",
                services: [],
                message: "",
                timeline: "",
                budget: ""
              }} validationRules={{
                name: {
                  required: true,
                  minLength: 2
                },
                company: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                services: {
                  required: true
                }
              }} submitText="Send Request" successMessage="Thank you — your inquiry has been received. Our team will contact you shortly." errorMessage="Something went wrong. Please try again.">
                  <InquiryForm />
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}